import { inject, isDevMode, Signal } from '@angular/core';

import { parseSchemaAddress } from '../address/schema-address';
import { dataAddressOf, injectSchemaResolver } from '../address/schema-resolver';
import { SchemaResolver } from '../address/schema-resolver.types';
import { DataState } from './data-state';
import { SCHEMA_FEATURE_FLAGS, SchemaFeatureFlags } from './schema-feature-flags';

/**
 * The comparisons a condition may name. Declared in one place because the blueprints copy it — each
 * one writes the list out, since editor-facing files cannot `$ref` across files — and
 * `schema-drift-guard.spec.ts` holds every copy to this array. Adding an operator is therefore an
 * entry here, an entry in `COMPARATORS`, and a test that names the blueprints still disagreeing.
 */
export const SCHEMA_CONDITION_OPERATORS = ['truthy', 'equals', 'notEquals', 'in'] as const;

export type SchemaConditionOperator = (typeof SCHEMA_CONDITION_OPERATORS)[number];

/**
 * Compares the value at one address. An omitted operator means `truthy`, as in all three dialects. `value` is resolved
 * like a data source's config: a whole-string address reads what it names — an enum member a host publishes in its
 * context, `$context.LockType.Cancellation` in kros-office — a list resolves each entry, and any other value is
 * compared as written.
 */
export interface SchemaComparisonCondition {
  path: string;
  operator?: SchemaConditionOperator;
  value?: unknown;
}

export interface SchemaAllCondition {
  all: SchemaCondition[];
}

export interface SchemaAnyCondition {
  any: SchemaCondition[];
}

export interface SchemaNotCondition {
  not: SchemaCondition;
}

/** Holds once the app's flags have loaded and this one is on; never while they load, never where no flags are provided. */
export interface SchemaHasFeatureFlagCondition {
  hasFeatureFlag: string;
}

/**
 * Holds once the app's flags have loaded and this one is off or unknown, and always where no flags are provided. Never
 * while they load — which `not` around `hasFeatureFlag` would, showing the old variant until the flags arrive.
 */
export interface SchemaHasNotFeatureFlagCondition {
  hasNotFeatureFlag: string;
}

/**
 * One condition language for the whole schema, and one key for it — `showIf`, wherever a condition may
 * be declared. Three dialects under two key names shipped separately before task 653017 folded them.
 *
 * `path` is an address read through `injectSchemaResolver()`, so a condition reaches whatever a component
 * reaches: the node's data by `$data`, the envelope by `$state`, the ambient context by
 * `$context`. That is what let `TileCondition.status` become `$state.status` rather than a second
 * comparator living beside the first. A feature flag is no address, so it has forms of its own (666120).
 */
export type SchemaCondition =
  | SchemaComparisonCondition
  | SchemaAllCondition
  | SchemaAnyCondition
  | SchemaNotCondition
  | SchemaHasFeatureFlagCondition
  | SchemaHasNotFeatureFlagCondition;

/** What a component holds after `injectSchemaCondition()`: the evaluator with its resolver already bound. */
export type SchemaConditionEvaluator = (condition: SchemaCondition | undefined, state: DataState | undefined) => boolean;

/**
 * The comparators, keyed by the operator that names them. Private on purpose: a registry open to
 * callers is what `predicate` was, and it reached production with zero registrations. Opening this
 * later is a token and a provider, so nothing is lost by waiting for a case that needs it.
 */
const COMPARATORS: Record<SchemaConditionOperator, (actual: unknown, expected: unknown) => boolean> = {
  truthy: (actual) => Boolean(actual),
  equals: (actual, expected) => actual === expected,
  notEquals: (actual, expected) => actual !== expected,
  in: (actual, expected) => isContainedIn(actual, expected),
};

/**
 * Answers whether a condition holds against the state a node was handed.
 *
 * A missing condition holds — a node that declares nothing is not a hidden node.
 *
 * @param schema the resolver, passed in rather than injected so this composes inside a `computed`.
 * `injectSchemaCondition()` is the usual way in.
 * @param featureFlags what `SCHEMA_FEATURE_FLAGS` holds, or `null` where nothing provides it. Read here, so a caller
 * evaluating inside a `computed` follows the flags.
 */
export function evaluateSchemaCondition(
  condition: SchemaCondition | undefined,
  state: DataState | undefined,
  schema: SchemaResolver,
  featureFlags: Signal<SchemaFeatureFlags | undefined> | null = null
): boolean {
  if (!condition) {
    return true;
  }

  if (isAllCondition(condition)) {
    return condition.all.every((nested) => evaluateSchemaCondition(nested, state, schema, featureFlags));
  }

  if (isAnyCondition(condition)) {
    return condition.any.some((nested) => evaluateSchemaCondition(nested, state, schema, featureFlags));
  }

  if (isNotCondition(condition)) {
    return !evaluateSchemaCondition(condition.not, state, schema, featureFlags);
  }

  if (isHasFeatureFlagCondition(condition)) {
    return isFeatureFlagOn(condition.hasFeatureFlag, featureFlags) === true;
  }

  if (isHasNotFeatureFlagCondition(condition)) {
    return isFeatureFlagOn(condition.hasNotFeatureFlag, featureFlags) === false;
  }

  return evaluateComparison(condition, state, schema);
}

/**
 * The evaluator for this node, holding what `injectSchemaResolver()` holds.
 *
 * A function rather than a class, like every other `inject*` helper here: there is no per-node state
 * to hold, and a class would need providing at every host that renders a schema — which is the one
 * thing `SurveyConditionEvaluator` needed, and it needed it only to reach a registry nobody wrote to.
 */
export function injectSchemaCondition(): SchemaConditionEvaluator {
  const schema = injectSchemaResolver();
  const featureFlags = inject(SCHEMA_FEATURE_FLAGS, { optional: true });

  return (condition, state) => evaluateSchemaCondition(condition, state, schema, featureFlags);
}

/**
 * Every comparison inside a condition, for callers that have to know what it compares before they run it —
 * the survey layout checks the step ids a slot compares the active step with.
 */
export function collectSchemaComparisons(condition: SchemaCondition | undefined): SchemaComparisonCondition[] {
  if (!condition) {
    return [];
  }

  if (isAllCondition(condition)) {
    return condition.all.flatMap(collectSchemaComparisons);
  }

  if (isAnyCondition(condition)) {
    return condition.any.flatMap(collectSchemaComparisons);
  }

  if (isNotCondition(condition)) {
    return collectSchemaComparisons(condition.not);
  }

  if (isHasFeatureFlagCondition(condition) || isHasNotFeatureFlagCondition(condition)) {
    return [];
  }

  return [condition];
}

/**
 * Every address a condition names, for callers that have to know what it reads before they run it —
 * the survey's forward-only check is the one today. Roots are returned as written, so a caller that
 * only means data has to skip the ones naming a root.
 */
export function collectSchemaConditionPaths(condition: SchemaCondition | undefined): string[] {
  return collectSchemaComparisons(condition).map((comparison) => comparison.path);
}

/**
 * The guard both `showIf` dialects opened with, kept but narrowed to the addresses it was about.
 *
 * `if (!data) return false` cannot be carried over literally: in the operator spelling `notEquals`
 * over a value that has not arrived is *true*, so every tile carrying one would blink into view while
 * its source loads — today it stays hidden. So a comparison **reading into the data** — what `dataOf`
 * names, the node's or its source's — does not hold while that data is missing, and one addressing an
 * envelope or the context still answers.
 *
 * A path that is not an address reads nothing, and the resolver says why. It holds under no operator:
 * under `notEquals` nothing differs from every value, so a bare path would show its node always.
 */
function evaluateComparison(condition: SchemaComparisonCondition, state: DataState | undefined, schema: SchemaResolver): boolean {
  if (typeof condition.path !== 'string') {
    reportShapelessCondition(condition);

    return false;
  }

  const [data] = schema.dataOf(condition.path);

  if (data && schema.read(dataAddressOf(data), state) == null) {
    return false;
  }

  const actual = schema.read(condition.path, state);
  const expected = schema.resolve(condition.value, state);

  if (namesNothing(condition.value, expected)) {
    reportUnresolvedValue(condition);

    return false;
  }

  return parseSchemaAddress(condition.path) !== null && COMPARATORS[condition.operator ?? 'truthy'](actual, expected);
}

/**
 * Whether an address written in `value` — the whole string, or an entry of a list — read nothing. An enum member the
 * host does not publish, or one that was renamed, lands here; under `notEquals` nothing would differ from every value
 * and the node would show on every row, so such a comparison holds under no operator.
 */
function namesNothing(value: unknown, resolved: unknown): boolean {
  if (typeof value === 'string') {
    return parseSchemaAddress(value) !== null && resolved === undefined;
  }

  if (Array.isArray(value) && Array.isArray(resolved)) {
    return value.some((entry, index) => namesNothing(entry, resolved[index]));
  }

  return false;
}

/** An address in `value` that reads nothing is a schema-authoring bug — a member nobody publishes — named in dev mode. */
function reportUnresolvedValue(condition: SchemaComparisonCondition): void {
  if (isDevMode()) {
    console.warn(
      `Schema condition ${JSON.stringify(condition)} compares with an address in "value" that reads nothing — a member ` +
        `no host publishes under the context, or one that was renamed. It does not hold under any operator.`
    );
  }
}

/**
 * The types say a comparison has a `path`, but a schema is JSON and reaches here untyped — a leftover
 * `valuePath` from the dialect this replaced lands exactly like this. Reported the way an unknown
 * address root is: named in dev mode, silent in production, and the node stays hidden either way.
 */
function reportShapelessCondition(condition: SchemaComparisonCondition): void {
  if (isDevMode()) {
    console.warn(
      `Schema condition ${JSON.stringify(condition)} names no "path", so it is not a comparison, an "all", an "any", ` +
        `a "not", a "hasFeatureFlag" or a "hasNotFeatureFlag". It does not hold. Conditions use { path, operator, value } since 653017.`
    );
  }
}

/**
 * Membership, and overlap where the value at the address is itself a list. The overlap half comes from
 * the onboarding evaluator, the only one of the four that had it — dropping it on the way in would
 * have made this a narrower language than one already in the repo.
 */
function isContainedIn(actual: unknown, expected: unknown): boolean {
  if (!Array.isArray(expected)) {
    return false;
  }

  return Array.isArray(actual) ? actual.some((entry) => expected.includes(entry)) : expected.includes(actual);
}

function isAllCondition(condition: SchemaCondition): condition is SchemaAllCondition {
  return 'all' in condition;
}

function isAnyCondition(condition: SchemaCondition): condition is SchemaAnyCondition {
  return 'any' in condition;
}

function isNotCondition(condition: SchemaCondition): condition is SchemaNotCondition {
  return 'not' in condition;
}

/**
 * Whether the flag `name` is on, per README-TECH §14: `true` once loaded and on, `false` once loaded and off or unknown
 * — as the flag store reads it — and `undefined` while the flags load. Where nothing provides the flags, every flag is off.
 * An unknown name and missing flags are both named in dev mode.
 */
function isFeatureFlagOn(name: string, featureFlags: Signal<SchemaFeatureFlags | undefined> | null): boolean | undefined {
  if (!featureFlags) {
    reportMissingFeatureFlags(name);

    return false;
  }

  const flags = featureFlags();

  if (flags && !Object.hasOwn(flags, name)) {
    reportUnknownFeatureFlag(name, flags);
  }

  return flags ? flags[name] === true : undefined;
}

/**
 * The flag service sends every flag it knows, off ones included — kros-office's initial user data lists them all — so a
 * name the loaded flags do not list is a typo, a deleted flag or one not created yet. Named in dev mode, silent in
 * production, and it reads as off either way.
 */
function reportUnknownFeatureFlag(name: string, flags: SchemaFeatureFlags): void {
  if (isDevMode()) {
    console.warn(
      `Schema condition names the feature flag "${name}", but the loaded flags (${Object.keys(flags).length}) do not list it, ` +
        `so it reads as off. Check the name, which is case-sensitive, against the flag service.`
    );
  }
}

/** No flags at all is a wiring bug, the way a missing SCHEMA_CONTEXT is. */
function reportMissingFeatureFlags(name: string): void {
  if (isDevMode()) {
    console.warn(
      `Schema condition names the feature flag "${name}", but no SCHEMA_FEATURE_FLAGS is reachable from this injector, ` +
        `so every flag reads as off. Add provideSchemaFeatureFlags(…) where this schema is rendered.`
    );
  }
}

function isHasFeatureFlagCondition(condition: SchemaCondition): condition is SchemaHasFeatureFlagCondition {
  return 'hasFeatureFlag' in condition;
}

function isHasNotFeatureFlagCondition(condition: SchemaCondition): condition is SchemaHasNotFeatureFlagCondition {
  return 'hasNotFeatureFlag' in condition;
}
